<template>
	<div class="chat-container">
		<DropZone v-if="dragging" @drop-files="onDropFiles" />

		<div class="message-list-wrapper">
			<MessageList
				:messages="messages"
				:isLoadingMore="false"
				:readReceiptsByMessageId="{}"
				:hideForwardAction="true"
				:hidePinAction="true"
				:hideCreateThread="true"
				:hideThreadElements="true"
				:isListVisible="showing"
				:class="{ 'zero-opacity': threadId }"
				@ref="messageList = $event"
				@thread="openThread"
				@reply="openReplyThread"
				@jump-to-latest="jumpToLatest"
			/>

			<ReplyChainView
				v-if="threadId"
				:replyChainId="threadId"
				:bubbleId="bubbleId"
				@close="threadId = null"
				@reply="openReplyThread"
			/>
		</div>

		<div class="typing-indicator-wrapper" style="position: relative">
			<div
				class="typing-participants"
				:class="{ 'is-typing': !threadId && typing }"
				v-text="typing"
			/>
		</div>
		<div
			class="chat-input-area"
			:class="{ suggestions: suggestionsShowing, disabled }"
		>
			<MediaPreview
				:mediaItems="mediaItems"
				animate
				small
				@remove="removeMediaItem"
				@reorder="reorderMediaItems"
			/>
			<Editor
				ref="chatinput"
				class="meetings-editor"
				v-model="messageText"
				:placeholder="placeholder"
				:disabled="disabled"
				:noEnter="isOverMax"
				@keypress="typingHandler"
				@enterPressed="sendMessage"
				@keyup.esc="clearFileData"
				@keyup.up="editLastMessage"
				@suggestionsChange="suggestionsShowing = $event"
				@resolvingMentions="needsToResolveMentions = $event"
			/>
			<div v-if="!disabled" class="chat-input-footer">
				<div>
					<MessageCharLimit
						:text="messageText"
						@over="isOverMax = true"
						@under="isOverMax = false"
					/>
				</div>
				<div class="chat-input-icons">
					<FilePicker
						class="file-picker"
						@files="onFiles"
						:multiple="true"
						ref="filePicker"
					>
						<PaperclipVerticalIcon type="regular" />
					</FilePicker>
					<EmojiPicker
						class="emojis"
						:options="{ perLine: 9, emojiSize: 18 }"
						@change="appendEmoji"
					>
						<FaceSmileIcon type="regular" />
					</EmojiPicker>
					<GiphyPicker class="gifs" @change="onGif" ref="giphyPicker">
						<GifIcon type="regular" />
					</GiphyPicker>

					<Menu
						@click.stop.prevent
						:items="menuItems"
						bottom
						ref="menu"
						:offsetY="-8"
						@select="onMenuItemSelect"
						menuClass="menu-input-attachments-menu"
					>
						<div class="actions">
							<CirclePlusIcon
								class="plus-icon"
								type="regular"
								:aria-label="$t('accessibility_toolbar_item_attachments_menu')"
							/>
						</div>

						<template #item="{ item }">
							<div class="option-item">
								<div class="item-label">{{ item.label }}</div>
								<component class="item-icon" :is="item.icon" type="regular" />
							</div>
						</template>
					</Menu>
				</div>
			</div>
		</div>
		<CreatePoll
			v-if="showCreatePoll"
			@close="showCreatePoll = false"
			:parentMessageId="threadId"
		/>
	</div>
</template>

<script>
import {
	store,
	getBubbleHistory,
	getCurrentUser,
	getUserMessages,
	getVideoSession,
	triggerTyping,
} from '../data'
import {
	FilePicker,
	GiphyPicker,
	showAlertModal,
	Menu,
} from '@/components/common'
import { post } from '@/helpers/api'
import { getTypingIndicatorText } from '@/helperMethods'
import moment from 'moment'
import DropZone from './DropZone'
import {
	GifIcon,
	PaperclipVerticalIcon,
	FaceSmileIcon,
	SquarePollHorizontalIcon,
	ClipboardListCheckIcon,
	CirclePlusIcon,
} from '@/icons'
import { closeNotificationsForBubble } from '../../../helpers/pushNotification'
import Editor from '../../Editor'
import MessageList from '../../Chat/MessageList'
import ReplyChainView from '../../Chat/ReplyChainView'
import {
	setMessage,
	getMyUserId,
	getMyMembership,
	updateMembership,
	getMostRecentMessage,
	getNonSystemMessages,
	getUsersForBubble,
} from '@/store'
import MessageCharLimit from '@/components/MessageInput/MessageCharLimit'
import EmojiPicker from '@/components/common/EmojiPicker'
import { chatCan, CREATE_MESSAGE, CREATE_POLLS } from '@/helpers/permissions'
import { sendMessage, prepFilesForUpload } from '@/api/message'
import {
	calcTotalProgressForMessageUUID,
	deleteUpload,
	setUpload,
} from '@/store/uploads'
import { MAX_MESSAGE_MEDIA } from '@/helpers/constants'
import { normalizeGiphyMediaItem, copyNativeFiles } from '@/helpers/utils'
import MediaPreview from '@/components/MessageInput/MediaPreview'
import { createLogger } from '@/helpers/log'
import CreatePoll from '@/components/Polls/CreatePoll'
import { orgFlag, POLLS } from '@/helpers/flags'

const log = createLogger('MeetingsChat', true)

export default {
	name: 'Messages',
	props: ['showing'],
	components: {
		DropZone,
		FilePicker,
		EmojiPicker,
		GiphyPicker,
		PaperclipVerticalIcon,
		FaceSmileIcon,
		GifIcon,
		Editor,
		MessageList,
		ReplyChainView,
		MessageCharLimit,
		MediaPreview,
		Menu,
		SquarePollHorizontalIcon,
		ClipboardListCheckIcon,
		CirclePlusIcon,
		CreatePoll,
	},
	data() {
		return {
			lastTypingSentTime: 0,
			uuid: null,
			dragging: false,
			messageText: '',
			suggestionsShowing: false,
			threadId: null,
			messages: [],
			messageList: null,
			isOverMax: false,
			bubbleId: store.bubble.id,
			mediaItems: [],
			tempUUID: crypto.randomUUID(),
			needsToResolveMentions: false,
			showCreatePoll: false,
		}
	},

	emits: ['unread'],

	provide() {
		return {
			maxMediaSize: 224, // Used by VisualMedia.vue & MediaGrid.vue
		}
	},

	watch: {
		filedata: function () {
			this.focus()
		},

		messages: {
			handler(msgs) {
				if (
					this.showing &&
					msgs.length > 0 &&
					msgs[0].sendState !== 'sending' &&
					msgs[0].sendState !== 'failed'
				) {
					this.updateMark()
				}
				this.$emit('unread', this.unread_ids.length)
			},
			deep: true,
		},

		showing() {
			this.updateMark()
			this.messageList.scrollTop = 0
		},

		threadId(id, old) {
			if (old && !id) {
				EventBus.$emit('clear-prepend-mention')
			}
		},
	},

	mounted() {
		const { host_id } = getVideoSession()
		const { id } = getCurrentUser()
		let historyFetched = false
		if (host_id !== id) {
			getBubbleHistory(store.bubble.id)
				.then((result) => {
					if (result?.ok !== false) historyFetched = true
				})
				.catch(() => {})
		}

		this.messages = getUserMessages()

		this.$onEventBus({
			meetingsDrag: (dragging) => {
				this.dragging = dragging
			},
			'message-change': () => {
				this.messages = getUserMessages()
			},
			videosessionUpdated: (videosession) => {
				if (!historyFetched && videosession.status === 'ACTIVE') {
					getBubbleHistory(store.bubble.id)
						.then((result) => {
							if (result?.ok !== false) historyFetched = true
						})
						.catch(() => {})
				}
			},
		})

		this.handlePaste = (e) => this.onPaste(e)

		window.addEventListener('paste', this.handlePaste, false)
	},

	beforeUnmount() {
		this.observer?.disconnect()
		window.removeEventListener('paste', this.handlePaste)

		for (var i in store.typinglist) {
			window.clearTimeout(store.typinglist[i].timeout)
		}

		store.typinglist = []
	},

	computed: {
		unread_ids() {
			const membership = getMyMembership()
			return this.messages
				.filter((m) => m.id > membership.mark)
				.map((m) => m.id)
		},

		typing() {
			return getTypingIndicatorText(store.typinglist.map((u) => u.user_id))
		},

		placeholder() {
			const currentChat = store.bubble
			if (currentChat?.isdm) {
				if (currentChat.dm_memberships_count > 2) {
					// Check if all DM members are inactive
					const allDeactivated = getUsersForBubble(currentChat.id)
						.filter((u) => u.id !== getMyUserId())
						.every((u) => u.deactivated_at !== null)
					if (allDeactivated) {
						return this.$t('chat_users_deactivated')
					}
				} else if (
					currentChat?.dmpartner?.id &&
					currentChat.dmpartner?.inactive
				) {
					return this.$t('chat_user_deactivated')
				}
			}

			if (!chatCan(currentChat, CREATE_MESSAGE)) {
				return this.$t('chat_input_text_placeholder_permission')
			} else if (this.threadId) {
				return this.$t('chat_input_text_placeholder_reply')
			} else {
				return this.$t('chat_input_text_placeholder_mentions')
			}
		},

		disabled() {
			const currentChat = store.bubble
			if (currentChat) {
				if (currentChat.isdm) {
					if (currentChat.dm_memberships_count > 2) {
						// Check if all DM members are inactive
						const allDeactivated = getUsersForBubble(currentChat.id)
							.filter((u) => u.id !== getMyUserId())
							.every((u) => u.deactivated_at !== null)
						if (allDeactivated) {
							return true
						}
					}
					return currentChat.dmpartner?.id && currentChat.dmpartner?.inactive
				} else {
					return !chatCan(currentChat, CREATE_MESSAGE)
				}
			}
			return false
		},

		menuItems() {
			const currentChat = store.bubble
			const canCreatePoll = chatCan(currentChat, CREATE_POLLS) && orgFlag(POLLS)

			return [
				...(canCreatePoll
					? [
							{
								id: 'poll',
								label: this.$t('common_menu_poll'),
								icon: SquarePollHorizontalIcon,
							},
					  ]
					: []),
				{
					id: 'gif',
					label: this.$t('common_menu_gif'),
					icon: GifIcon,
					class: 'gif-menu-item',
				},
				{
					id: 'file',
					label: this.$t('common_menu_file'),
					icon: PaperclipVerticalIcon,
				},
			]
		},
	},

	methods: {
		reorderMediaItems(orderedTempIds) {
			log('user reordered media', orderedTempIds)
			if (!this.mediaItems?.length) return
			this.mediaItems = orderedTempIds.map((tempId) =>
				this.mediaItems.find((m) => m.tempId === tempId)
			)
		},
		removeMediaItem({ tempId }) {
			log('user removed media', tempId)
			deleteUpload(tempId)
			// We have to remove the item in place using .splice() because SortableJS doesn't
			// like it when the original array gets replaced wholesale when using .filter()
			const index = this.mediaItems.findIndex((m) => m.tempId === tempId)
			this.mediaItems.splice(index, 1)
		},

		openThread(message) {
			this.threadId = message.threadId
			this.focus()
		},

		openReplyThread(message) {
			EventBus.$emit('prepend-mention', message.user_id)
			this.openThread(message)
		},

		clearFileData() {
			this.messageItems = []
		},

		onDropFiles(files) {
			log('user dropped files', copyNativeFiles(files))
			this.dragging = false
			this.onFileChange(files)
		},

		onFiles(files) {
			log('user selected files', copyNativeFiles(files))
			this.onFileChange(files)
		},

		onGif(giphyData) {
			log('selected giphy image', giphyData)
			if (!giphyData || !giphyData.images) return
			if (this.mediaItems.length >= MAX_MESSAGE_MEDIA) {
				log('giphy exceeds max count', this.mediaItems.length)
				return showAlertModal({
					title: this.$t('chat_attachment_max_limit_exceeded_title'),
					description: this.$t(
						'chat_attachment_max_limit_exceeded_description',
						{ Count: MAX_MESSAGE_MEDIA }
					),
				})
			}
			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const mediaItem = {
				...normalizeGiphyMediaItem(giphyData),
				isComplete: true,
				isNormalized: true,
				messageUUID: this.tempUUID,
			}
			delete mediaItem.uuid
			setUpload(mediaItem.tempId, {
				tempId: mediaItem.tempId,
				mediatype: mediaItem.mediatype,
				messageUUID: mediaItem.messageUUID,
				uploadProgress: 100,
				isComplete: true,
				isNormalized: true,
			})
			this.mediaItems.push(mediaItem)
			this.focus()
		},

		onPaste(e) {
			const { items = [] } = e.clipboardData
			// filter out any content that isn't an image
			log('user pasted files', items)
			const images = Array.from(items)
				.filter(({ type }) => type && type.includes('image'))
				.map((item) => item.getAsFile())
				.filter((file) => file)
			this.onFileChange(images)
		},

		async onFileChange(files = []) {
			if (!files?.length) return false

			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const normalizedMedia = await prepFilesForUpload(
				files,
				this.mediaItems.length,
				this.tempUUID
			)

			normalizedMedia.forEach((media) => {
				this.mediaItems.push(media)
			})

			this.$nextTick(() => this.focus())
		},

		async sendMessage(messageText) {
			log('attempting to send message', messageText)
			if (this.isOverMax) return

			if (!messageText.replace(/\s/g, '').length && !this.mediaItems.length) {
				return
			}

			const message = this.generateMessagePayload()
			log('attempting to send message', message)
			setMessage(message)

			this.messageText = ''
			this.tempUUID = null

			if (this.mediaItems.length) {
				message.messagemedia = this.mediaItems
				setMessage(message)
				this.mediaItems = []

				const progress = calcTotalProgressForMessageUUID(message.uuid)
				// if we haven't finished uploading files, then we'll just skip out now
				if (progress < 100) return
			}

			try {
				await sendMessage(message)
			} catch (e) {}
		},

		generateMessagePayload(data = {}) {
			const payload = {
				id: null,
				uuid: this.tempUUID || crypto.randomUUID(),
				bubble_id: store.bubble.id,
				message: this.messageText.trim(),
				created_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
				sendState: 'waiting',
				user_id: getMyUserId(),
				systemevent: null,
				...data,
			}

			if (this.threadId) {
				payload.parentmessage_id = this.threadId
			}

			if (this.needsToResolveMentions) {
				const containsPotentialMention = ASYNC_MENTIONS_REGEXP.test(
					payload.message
				)
				payload.replace_string_mentions = containsPotentialMention
			}

			return payload
		},

		appendEmoji(emoji) {
			this.$refs.chatinput.insertEmoji(emoji.emoji)
			this.focus()
		},

		focus() {
			EventBus.$emit('focus-editor')
		},

		editLastMessage() {
			if (!['', '\n'].includes(this.messageText)) return

			const myId = getMyUserId()
			const myLastMessage = getNonSystemMessages()
				.slice()
				.reverse()
				.find((m) => m.user_id === myId)

			if (!myLastMessage) return

			EventBus.$emit('editLatestMessage', myLastMessage.id)
		},

		typingHandler() {
			if (this.lastTypingSentTime < moment().unix() - 4) {
				this.lastTypingSentTime = moment().unix()
				triggerTyping(this.$pusher)
			}
		},

		updateMark() {
			if (this.threadId || this.unread_ids.length === 0) return
			post('/api/v1/bubble.mark', {
				bubble_id: store.bubble.id,
				message_id: this.unread_ids[0],
			})
				.then(({ data }) => {
					if (data.ok) {
						this.setUserMark()
						closeNotificationsForBubble(store.bubble.id)
					}
				})
				.catch((error) => {
					console.error(error)
				})
		},

		updateMessage(message) {
			this.uuid = message.uuid
			setMessage(message)
		},

		setUserMark() {
			const { id: mark } = getMostRecentMessage()
			const { id: membId } = getMyMembership()
			updateMembership(membId, { mark })
			this.$emit('unread', this.unread_ids.length)
		},

		async jumpToLatest() {
			// wait for latest messages to render
			await this.$nextTick()
			// immediately scroll all the way to the bottom
			this.messageList.scrollTop = 0
		},

		onMenuItemSelect(item) {
			switch (item.id) {
				case 'gif':
					this.$refs.giphyPicker.showGiphy()
					break
				case 'file':
					this.$refs.filePicker.$refs.input.click()
					break
				case 'poll':
					this.showCreatePoll = true
					break
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
@import './common';

@mixin wrapped-actions {
	flex-flow: wrap;
	justify-content: space-between;

	.pronto-editor {
		margin-bottom: 8px;
		min-width: 100%;
	}

	.edit-actions {
		flex-direction: row-reverse;

		.char-limit {
			top: 0;
			left: -55px;
		}
	}

	.pronto-btn {
		width: 28px;
		height: 26px;
		margin-left: 6px;
		box-shadow: unset;
	}

	.emoji-selector {
		padding: unset;
		border: unset;
		border-left: unset;
		border-top-right-radius: unset;
		border-bottom-right-radius: unset;
		background-color: unset;
	}
}

.chat-input-area {
	&.suggestions {
		border-radius: 0;
		border-top: 1px solid #2f302f;
	}
	&.disabled {
		pointer-events: none;
		user-select: none;
	}
	:deep(.media-preview-container) {
		padding-left: 0;
		.media-item {
			max-width: 80%;
		}
		.remove {
			background-color: var(--text-gray-medium);
			.pronto-icon {
				color: white;
			}
		}
		.video-overlay {
			.pronto-icon {
				font-size: 24px;
			}
		}
		.media-file-container {
			max-width: 100%;
		}
	}
}

.meetings-editor {
	width: 100% !important;
	:deep(.ql-editor) {
		padding: 0;
		margin-bottom: 35px;
		&::before,
		* {
			font-family: 'Avenir-Medium';
			color: white;
			font-size: 14px;
		}
		&::before {
			color: #7e7e7e;
			left: auto;
			right: auto;
		}
	}

	@include mention-completions;

	:deep(.quill-mention) {
		background: rgba($pronto-blue, 0.16);
		* {
			color: $pronto-blue !important;
		}
		&.mention-me {
			background: rgba(#ffbb00, 0.18);
		}
	}

	@include emoji-completions;

	:deep(.mention-completions),
	:deep(.emoji-completions) {
		width: calc(100% + 16px);
		left: -8px;
		z-index: 999999;
	}
}

.chat-container {
	display: flex;
	flex-direction: column;
	height: calc(100vh - 32px - 46px);
	position: relative;
	:deep(.close-thread-pill) {
		left: unset !important;
		top: 95px !important;
		right: 0;
		z-index: 10;
	}
}

.message-list-wrapper {
	position: relative;
	flex-grow: 1;
	overflow: auto;
	display: flex;
	:deep(.message-img) {
		--size: 38px !important;
		--online-size: 12px !important;
	}

	:deep(.message-sending) {
		&.active {
			padding: 2px 0 4px 2px;
			margin: 0;
			background: none;
		}
	}
	:deep(.message-list) {
		padding: 0 8px 30px 8px;
	}

	:deep(.message-wrapper) {
		&.failedwrapper {
			background: inherit;
		}
		.message-img {
			font-size: 22px;
			border-color: #222 !important;
		}
	}

	:deep(.reaction-list) {
		margin-right: 4px !important;
	}

	:deep(.emojis) {
		right: 12px !important;
		background: #000 !important;
		&::after {
			background: linear-gradient(
				to right,
				transparent 0%,
				#000 100%
			) !important;
		}
	}
}

.typing-participants {
	bottom: 0;
	height: 0;
	font-family: Avenir-Medium;
	font-size: 11px;
	line-height: 18px;
	color: #7e7e7e;
	padding: 0 12px;
	max-width: 100%;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	transition: height 0.3s ease-in-out;
	background-color: #222;
	width: 100%;
	&.is-typing {
		height: 18px;
	}
	position: absolute;
	z-index: 1000;
}

.chat-input-area {
	position: relative;
	margin: 0 8px 8px 8px;
	padding: 0 12px 12px 12px;
	border-radius: 8px;
	font-family: Avenir-Medium;
	font-size: 14px;
	background-color: #111111;
	.chat-input-footer {
		@include flex-spread;
		.chat-input-icons {
			@include flex;
			justify-content: flex-end;
			gap: 16px;
			margin-top: 4px;
			.file-picker,
			.emojis,
			.gifs,
			.plus-icon {
				cursor: pointer;
				font-size: 24px;
				height: 24px;
				color: var(--neutral-400);
				&:hover {
					color: var(--neutral-600);
				}
			}
			.gifs {
				margin-top: -7px;
				font-size: 30px;
			}
			.file-picker {
				width: 18px;
			}
		}
		:deep(.emojis) {
			.emoji-mart {
				right: -66px;
				&::before {
					right: 68px;
				}
			}
		}
	}
}

.messages-attachment {
	position: relative;
	width: unset;
	border-top: unset;
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	background-color: #111;

	:deep(.video-preview) {
		display: block;
		width: 260px;
		margin: 16px auto 0 auto;
	}
	:deep(.image-preview) {
		margin-left: 0;
		width: 260px;
	}
}

.messages-attachment > .attachment-wrapper {
	margin-left: 10px !important;
}

.no-messages {
	position: absolute;
	top: calc(50% - 20px);
	left: 40px;
	font-family: Avenir-Medium;
	font-size: 14px;
	opacity: 41%;
}

:deep(.url-card) {
	.attachment-link {
		display: block;
	}
	.unfurl-card-horz {
		max-width: 100%;
		.thumbnail-img {
			max-width: 70px;
		}
		.unfurl-snippet {
			color: #63656d !important;
		}
	}
}

.menu-item:first-of-type .option-item {
	margin-top: 5px;
}
.menu-item:last-of-type .option-item {
	margin-bottom: 5px;
}
.option-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
	min-width: 110px;
	color: var(--neutral-800);
	padding: 4px 4px 4px 8px;

	.item-label {
		text-align: left;
		font-family: 'Avenir-Medium';
		font-size: 16px;
	}

	.item-icon {
		text-align: right;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		min-width: 24px;
		margin-left: 8px;
		font-size: 16px;
		color: var(--neutral-800);
	}

	&:hover {
		background-color: var(--neutral-100);
		border-radius: 6px;
		cursor: pointer;
	}
}

:deep(.message-poll-card) {
	background-color: #111 !important;
	&:hover {
		background-color: #222 !important;
	}
}

// Dark mode override styles for messages
$left-offset: 18px;

@include mention-completions;
@include emoji-completions;

:deep(.message-outer-container) {
	&.trimmed {
		.message-actions {
			top: unset !important;
			bottom: 16px !important;
		}
	}
	&.editing {
		.message-wrapper {
			&.message-hover {
				border-radius: 8px;
			}
		}
	}
	.message-wrapper {
		$back: #222;
		padding-left: 0;
		&.message-hover {
			background-color: #000;
			border-radius: 8px 0 8px 8px;
			.message-img {
				border-color: #000 !important;
			}
			.message-poll-card {
				background-color: #222 !important;
			}
		}
		&:hover {
			.message-img {
				border-color: #000;
			}
		}
		&.stacked .message {
			margin-left: 45px;
		}
		&.stacked .message-actions-wrapper {
			top: 0;
		}
		&.stacked .message-edit {
			margin-top: 6px;
		}

		.message-edit {
			@include wrapped-actions();
			width: 218px;

			.pronto-editor {
				font-size: 14px;
				color: #fff;
				background: #222222;
				border-color: #2c2d31;
				border-radius: 5px !important;

				.ql-editor {
					padding: 0;
					margin-bottom: 35px;
					&::before,
					* {
						font-family: 'Avenir-Medium';
						color: white;
						font-size: 14px;
					}
					&::before {
						color: #7e7e7e;
						left: auto;
						right: auto;
					}
				}

				.quill-mention {
					background: rgba($pronto-blue, 0.16);
					* {
						color: $pronto-blue !important;
					}
					&.mention-me {
						background: rgba(#ffbb00, 0.18);
					}
				}
			}

			.emojis {
				.emoji-mart {
					width: 298px !important;
					height: 300px !important;
					right: -226px;
					&::before {
						right: 227px;
					}
				}
			}

			@media screen and (max-width: 800px) {
				@include wrapped-actions();
			}
		}

		.message-img {
			border-color: $back;
		}
		.message {
			min-width: unset;
			max-width: 100%;
			overflow: hidden;
			.message-header {
				.message-name {
					font-size: 14px;
					color: var(--blue-300);
					font-family: Avenir-Medium;
					max-width: calc(100% - 60px);
					overflow: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
				}
			}
		}
		.poll-card {
			width: 285px !important;
		}
		.message-text {
			color: white;
			width: 285px;
			font-size: 14px;
			padding: 0;
			.message-content {
				max-width: calc(100% - 16px);
			}
			.message-media-container {
				--file-width: 285px;
			}
		}
		.attachment-title {
			color: white;
		}
		.message-actions-wrapper {
			right: 8px;
			.message-actions {
				top: 16px;
				right: 8px;
				bottom: unset;
				background: #000;
				border-radius: 8px 8px 0 0;
				box-shadow: 0 0 13px 0 rgba(0, 0, 0, 0.5);
				.emojis {
					&:after {
						right: 26px;
					}
				}
			}
		}
		.reaction-extension {
			&.expanded {
				width: 140px;
			}
		}
		.reactions {
			margin-left: 44px;
		}
		.hover-backdrop {
			background: #000;
		}
		.thread-line {
			left: $left-offset;
		}
		.thread-line-dots {
			left: $left-offset;
		}
		.message-img .online-indicator {
			opacity: 0 !important;
		}
		.message-translation-background {
			background-color: rgba(73, 164, 218, 0.2);
		}
		.audio-player-container {
			width: calc(100% - 12px);
			border-color: var(--neutral-800);
			--buffered-color: #{$neutral-300} !important;
			--unbuffered-color: #{$text-gray-dark} !important;
			.time {
				color: white;
			}
		}
	}
	.view-thread-btn {
		margin-left: $left-offset;
	}
}

:deep(.thread-message-list) {
	$back: #333;
	background-color: $back;
	.thread-header {
		z-index: 5;
		top: 52px;
		right: 16px;
		left: unset;
		width: 349px;
		height: 44px;
		background: $back;
		border-bottom-color: #313131;
		.thread-label {
			font-size: 14px;
		}
		svg {
			width: 14px;
			height: 14px;
			--color: white !important;
		}
	}
	.message-img {
		border-color: $back !important;
	}
}

:deep(.parent-quote) {
	margin-left: 0 !important;
	.parent-user {
		color: #fff !important;
	}
	.parent-text {
		font-family: Avenir-Roman;
	}
	.parent-content {
		color: #7e7e7e;
		background-color: #121212 !important;
	}
}

:deep(.ql-editor.ql-blank::before) {
	font-size: 14px !important;
}
:deep(.sendingplaceholder) {
	max-width: 225px;
}
</style>
